/**
 * The crash course (board learn-r2, answers 6 and 8): how the pieces work under your app. Short, plain lessons that
 * name the real tools (git, GitHub, Cloudflare Tunnel, Railway, Bun, Vite), each with a mission to try it.
 */
import type { Mission } from '../missions'
import { FLAG_US, savedOnly, selfTick } from './shared'

export const crashCourse: Mission[] = [
  {
    id: 'crash-git',
    order: 1,
    title: 'Git: save points for your app',
    track: 'crash-course',
    summary: 'What git is, and the four things your agent does with it: commit, branch, push and undo.',
    lesson: `**Git** keeps the history of your app. Each **commit** is a save point: what changed, who changed it, and a note that says why. Your agent makes commits for you. Once something is committed, it can always be found again.

- **Commit** (\`git commit\`): a save point with a one-line note. Small ones are easier to read and to undo.
- **Branch** (\`git switch -c\`): a side line for a bigger change, so the main line keeps working while it's built.
- **Push** (\`git push\`): sends your commits to **GitHub**. A push to \`main\` is what ships your app.
- **Undo** (\`git revert\`): a new commit that reverses an old one. History is never rewritten, so nothing disappears.

**GitHub** holds the copy everyone shares: your computer's, your teammates', and the one Railway builds your live app from. Your computer signs in to GitHub by itself; there is no password to type.`,
    say: "Show me my app's last five commits in plain words, and tell me if anything isn't saved yet.",
    happens: [
      'Your agent runs git log and git status.',
      'It tells you each commit as one plain sentence.',
      "It says whether anything is changed but not committed, or committed but not pushed.",
      'If something is left over, ask it to commit it with a short note.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once your agent has answered.',
    ifNot: [
      { symptom: 'The notes are full of code words.', say: 'Say each commit again as one plain sentence about what I would see.' },
      { symptom: 'The agent wants to force-push or reset.', say: "Don't rewrite history. If something needs undoing, make a new commit that reverses it." },
      FLAG_US,
    ],
    how: selfTick('Git: save points for your app', "This page can't see your chat."),
    recipe: 'crash-course/git',
  },
  {
    id: 'crash-preview-link',
    order: 2,
    title: 'Your preview link',
    track: 'crash-course',
    summary: 'The dev server, hot reload, the Cloudflare Tunnel, and why the link is private.',
    lesson: `Your app runs in two places. The **preview** runs on your cpg.computer computer while you and your agent work. The **live app** runs on Railway, for your team.

The preview is the **dev server** (\`bun run dev\`, built on **Vite**). Each time a file is saved, it rebuilds just that part and swaps it into the open page. That's **hot reload**: no refresh, and the change shows in a second or two.

A **Cloudflare Tunnel** joins your computer to your preview address, \`https://<your-brand>-preview.cpg.computer\`. Your computer opens no door to the internet: the tunnel calls out to Cloudflare, and Cloudflare sends visitors back along it.

**The link is private.** Anyone can type the address, but every page asks them to sign in, and only people on your app's list get in. Sign-in can't be switched off on your computer.

If the dev server stops, the link shows an error until it starts again. Your live app keeps running either way.`,
    say: 'Start the preview, send me the link, and tell me in two sentences how that link reaches my computer.',
    happens: [
      'Your agent starts the dev server, if it isn\'t running.',
      'It sends you your preview link and explains the tunnel in plain words.',
      'Open the link and sign in.',
      'Open this lesson there, and it ticks.',
    ],
    check: { kind: 'host' },
    watching: "This page's address, waiting for one that ends in -preview.cpg.computer.",
    ifNot: [
      { symptom: "The link doesn't load.", say: 'Is the dev server running? Start it again and check that my preview link answers.' },
      FLAG_US,
    ],
    how: `The page reads its own address. Nothing is sent anywhere to check it. ${savedOnly('Your preview link')}`,
    recipe: 'crash-course/preview-link',
  },
  {
    id: 'crash-deploy',
    order: 3,
    title: 'Deploy and hosting',
    track: 'crash-course',
    summary: 'What happens after a push: the build, the deploy, your address and the logs.',
    lesson: `**Shipping** puts a change on your live address. After your agent pushes to \`main\` on GitHub, four things happen by themselves:

- **Build**: **Railway** takes the new code, installs it with **Bun**, and runs \`vite build\`, which packs your pages into small files.
- **Deploy**: Railway starts the new version. Your team keeps using the old one until the new one is up.
- **Your address**: your live app is at \`https://<your-brand>.cpg.computer\`. It always runs the newest version that built. \`/healthz\` on it says which version that is.
- **Logs**: each request and each error is a line in Railway's logs. Your agent reads them when something looks wrong.

**If a build fails, the old version keeps running.** Nothing breaks for your team; the change just isn't live yet. Your agent reads the build log, fixes the cause and ships again.

Your app's keys reach Railway from your dashboard. You never log in to Railway yourself.`,
    say: 'Walk me through my last ship: what was built, when it went live, and anything odd in the logs.',
    happens: [
      'Your agent finds your last push.',
      "It checks the version your live address runs against that push.",
      'It reads the latest logs for errors.',
      'It tells you all of it in a few plain sentences.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once your agent has answered.',
    ifNot: [
      { symptom: "The live version doesn't match.", say: 'Is the deploy still running, or did the build fail? Read the build log and tell me.' },
      FLAG_US,
    ],
    how: selfTick('Deploy and hosting', "This page can't see your chat."),
    recipe: 'crash-course/deploys',
  },
  {
    id: 'crash-dev-and-prod',
    order: 4,
    title: 'Dev and prod',
    track: 'crash-course',
    summary: 'The preview against the live app, env files, keys that never go in code, and the VITE_ trap.',
    lesson: `**Dev** (development) is your preview: it runs on your computer, rebuilds as you go, and is where you try things. **Prod** (production) is your live app on Railway, the one your team uses every day. Changes go one way: dev, then \`bun run check\`, then ship to prod.

Each place reads its settings from its own **environment**:

- On your computer, from \`.env.local\`. cpg.computer writes its part between two marker lines; lines you add stay. It is never committed.
- On Railway, from its variables. cpg.computer sets them when you add a key in your dashboard.

**Keys never go in the code.** Not in a file, not in a commit, not in the chat. Anything committed stays in history for good, so a key that slips in has to be replaced. Add keys in your dashboard under Connections, then Keys. Your app reads them on the server, from \`process.env\`.

**The VITE_ trap.** Vite copies every setting whose name starts with \`VITE_\` into the pages it sends to browsers, where anyone can read it. A key named \`VITE_…\` is a public key. cpg.computer refuses those names, and the house rules fail the check if one turns up.`,
    say: 'Which keys does my app use, where is each one kept, and is any of them in the code or named VITE_?',
    happens: [
      'Your agent lists the names your app reads, never the values.',
      'For each, it says where it lives: .env.local on your computer, and Railway for the live app.',
      "It checks nothing shaped like a key is in the code, and that no key's name starts with VITE_.",
      'If it finds one, it tells you how to fix it, starting with replacing the key.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once your agent has answered.',
    ifNot: [
      { symptom: 'The agent shows you a key.', say: "Don't show me key values. Names only. That key needs replacing now: tell me where." },
      FLAG_US,
    ],
    how: selfTick('Dev and prod', "This page can't see your chat."),
    recipe: 'crash-course/dev-and-prod',
  },
  {
    id: 'crash-hygiene',
    order: 5,
    title: 'Developer hygiene',
    track: 'crash-course',
    summary: 'Small commits, reading the diff, tests, the house rules, STATUS.md, and when to flag us.',
    lesson: `Good habits keep an app safe after many hands, yours and your agent's. Ask for these every time:

- **Small commits.** One change per commit, with a note that says why. Small changes are easy to read, test and undo.
- **Read the diff.** Before you ship, say "show me what changed". The **diff** lists the lines added and removed.
- **Tests.** Every screen has a test for its numbers. A new number gets a new test.
- **One check.** \`bun run check\` runs types, tests, the house rules and the build in one go. Green before every preview and every ship. It runs again before each push, and on GitHub.
- **The house rules** (\`docs/rules.md\`). Never change a rule to get green. Fix the code, or agree an exception with a reason.
- **STATUS.md.** A short note in your app: where things stand, what's half done, what's next. Your agent reads it at the start of each task and rewrites it at the end, so the next chat picks up where this one stopped.

**When to flag us:** a rule blocks something you need, a mission looks broken, sign-in or keys act strangely, or the same error comes back twice. Use "Flag us" in your dashboard with the note your agent writes.`,
    say: 'Run bun run check and tell me in plain words what it found. Then show me what you changed today, and update STATUS.md.',
    happens: [
      'Your agent runs bun run check and tells you what passed and what failed.',
      "It shows you today's changes in plain words, one line each.",
      'It rewrites STATUS.md: now, in flight, next, blockers.',
      'Anything it wants to fix, it asks you first.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once your agent has answered.',
    ifNot: [
      { symptom: 'The check fails.', say: 'Tell me which rule failed and why, then fix the code. Leave the rule as it is.' },
      FLAG_US,
    ],
    how: selfTick('Developer hygiene', "This page can't see your chat."),
    recipe: 'crash-course/hygiene',
  },
]
